/**
 * Pdf — Tag o1x first-class pour les documents PDF (ha/429).
 *
 * Point d'extension unique de tout ce qui touche aux PDF dans le runtime
 * (sur le modèle de UImg/Gallery). Enregistré `pdf` dans HtmlService.
 *
 * Le root rendu est un `<a href>` (fallback accessible natif : clic-milieu =
 * nouvel onglet, fonctionne JS off). Selon `mode` :
 *   - **modal** (défaut, 1ère capacité livrée) : clic gauche → overlay iframe.
 *   - futurs : `inline` (embed), `download`, `thumb`… (l'API les prévoit, non livrés).
 *
 * Résolution de l'URL (resolveUrl) :
 *   1. `src` (clé top-level, nœud programmatique)
 *   2. attribut `href` (cas mockup : posé par le converter, ou par data-bind-href
 *      côté serveur lors de l'hydratation des listes)
 *   3. `reference` + `doc` (facture|proforma) → `/admin/agency/<doc>/pdf?reference=…`
 *
 * Nœud JSON typique :
 *   { "t":"pdf", "label":"Voir le PDF", "mode":"modal",
 *     "attributes":[{"n":"href","v":"/admin/agency/facture/pdf?reference=FA-2026-001"}] }
 *   ou { "t":"pdf", "reference":"FA-2026-001", "doc":"facture" }
 */

import { Container } from "../Container/Container";

export class Pdf extends Container {

    private static current: HTMLElement | null = null;
    private static escHandler: ((e: KeyboardEvent) => void) | null = null;

    public getTagName(): string {
        return "a";
    }

    protected createSelf(): void {
        super.createSelf();
        this.addClass("ha-pdf");

        const url = this.resolveUrl();
        const label = this.translate(this.get("label")) || "Voir le PDF";
        const mode = (this.get("mode") || "modal") as string;

        // Fallback accessible : le root <a href> ouvre le PDF (clic-milieu, JS off).
        if (url) {
            this.setAttribute("href", url);
        }
        this.setAttribute("target", "_blank");
        this.setAttribute("rel", "noopener");
        if (!this.has("txt") && this.countChildren() === 0) {
            this.setText(label);
        }

        if (mode === "modal" && url) {
            // Marqueur pour la délégation globale (cf. bindDelegation) : indispensable
            // pour les liens rendus côté SERVEUR (hydrateSlot/SSR) où createSelf ne tourne
            // pas client-side → l'onClick par-instance ci-dessous ne suffit pas seul.
            this.setAttribute("data-pdf-modal", "1");
            this.onClick((event: MouseEvent) => {
                event.preventDefault();
                event.stopImmediatePropagation();
                Pdf.openModal(url, label);
            });
        }
    }

    private static delegationBound = false;

    /**
     * Listener délégué global : intercepte les clics sur tout `a[data-pdf-modal]`,
     * y compris les liens rendus côté serveur (SSR/hydrateSlot) pour lesquels la
     * classe Pdf n'est jamais instanciée client-side. Bindé une fois au boot
     * (cf. initInteractions). Sans ça, le root `<a target="_blank">` ouvre un onglet.
     */
    public static bindDelegation(): void {
        if (Pdf.delegationBound) { return; }
        Pdf.delegationBound = true;
        document.addEventListener("click", (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            const link = target?.closest("a[data-pdf-modal]") as HTMLAnchorElement | null;
            if (!link) { return; }
            const url = link.getAttribute("href") || "";
            if (!url) { return; }
            event.preventDefault();
            Pdf.openModal(url, (link.textContent || "").trim() || undefined);
        });
    }

    private resolveUrl(): string {
        const src = this.translate(this.get("src"));
        if (src) { return src; }
        const href = this.getAttribute("href");
        if (href) { return href; }
        const reference = this.get("reference");
        const doc = this.get("doc");
        if (reference && doc) {
            return `/admin/agency/${doc}/pdf?reference=${encodeURIComponent(String(reference))}`;
        }
        return "";
    }

    // ─── 1ère capacité : viewer modal (overlay iframe) ───────────────────────

    public static openModal(url: string, label?: string): void {
        Pdf.closeModal();

        const overlay = document.createElement("div");
        overlay.className = "ha-pdf-modal";
        overlay.setAttribute("role", "dialog");
        overlay.setAttribute("aria-modal", "true");
        overlay.setAttribute("aria-label", label || "Document PDF");

        const dialog = document.createElement("div");
        dialog.className = "ha-pdf-modal__dialog";

        const close = document.createElement("button");
        close.type = "button";
        close.className = "ha-pdf-modal__close";
        close.setAttribute("aria-label", "Fermer");
        close.innerHTML = "&times;";

        const frame = document.createElement("iframe");
        frame.className = "ha-pdf-modal__frame";
        frame.setAttribute("src", url);
        frame.setAttribute("title", label || "Document PDF");

        dialog.appendChild(close);
        dialog.appendChild(frame);
        overlay.appendChild(dialog);
        document.body.appendChild(overlay);

        close.addEventListener("click", () => Pdf.closeModal());
        overlay.addEventListener("click", (e: MouseEvent) => {
            if (e.target === overlay) { Pdf.closeModal(); }
        });
        Pdf.escHandler = (e: KeyboardEvent) => {
            if (e.key === "Escape") { Pdf.closeModal(); }
        };
        document.addEventListener("keydown", Pdf.escHandler);

        Pdf.current = overlay;
        close.focus();
    }

    public static closeModal(): void {
        if (Pdf.escHandler) {
            document.removeEventListener("keydown", Pdf.escHandler);
            Pdf.escHandler = null;
        }
        if (Pdf.current && Pdf.current.parentNode) {
            Pdf.current.parentNode.removeChild(Pdf.current);
        }
        Pdf.current = null;
    }
}
